import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle, XCircle, ChevronDown, ChevronUp, Sparkles, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { apiRequest } from "@/lib/queryClient";
import { asArray } from "@/lib/utils";
import type { AgentTask } from "@/types/agent";

interface ActivityLogEntry {
  id: number;
  taskId: number | null;
  stepType: string;
  stepName: string;
  outputSummary: string | null;
  createdAt: string;
}

const STEP_TYPE_COLORS: Record<string, string> = {
  tool_call: "bg-blue-100 text-blue-700 border-blue-200",
  model_call: "bg-violet-100 text-violet-700 border-violet-200",
  mutation: "bg-green-100 text-green-700 border-green-200",
};

function ActivityFeed() {
  const { data, isLoading } = useQuery<ActivityLogEntry[]>({
    queryKey: ["agent-activity"],
    queryFn: async () => {
      const res: any = await apiRequest("GET", "/api/agents/activity");
      return asArray<ActivityLogEntry>(res.data);
    },
    refetchInterval: 30000,
  });

  const entries = asArray<ActivityLogEntry>(data);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full rounded-xl" />)}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="text-center py-16 text-gray-400" data-testid="activity-empty">
        <Activity className="h-8 w-8 mx-auto mb-3 text-gray-300" />
        <p className="font-medium text-gray-500">No agent activity yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="activity-feed">
      {entries.map((entry) => {
        const colorClass = STEP_TYPE_COLORS[entry.stepType] ?? "bg-gray-100 text-gray-600 border-gray-200";
        const summary = entry.outputSummary ?? "";
        return (
          <div key={entry.id} className="bg-white border border-gray-100 rounded-lg px-4 py-3 flex items-start gap-3 shadow-sm">
            <Badge className={`${colorClass} border text-xs shrink-0 mt-0.5`}>{entry.stepType}</Badge>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-800">{entry.stepName}</p>
              {summary && (
                <p className="text-xs text-gray-400 truncate mt-0.5">{summary.slice(0, 120)}</p>
              )}
            </div>
            <span className="text-xs text-gray-400 shrink-0">
              {new Date(entry.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
        );
      })}
    </div>
  );
}

type StatusFilter = "all" | "awaiting_approval" | "approved" | "rejected";

const STATUS_LABELS: Record<StatusFilter, string> = {
  all: "All",
  awaiting_approval: "Awaiting approval",
  approved: "Approved",
  rejected: "Rejected",
};

function statusBadge(task: AgentTask) {
  if (task.status === "awaiting_approval") {
    return <Badge className="bg-amber-100 text-amber-800 border-amber-200 border text-xs">Pending review</Badge>;
  }
  if (task.approvalStatus === "approved") {
    return <Badge className="bg-green-100 text-green-800 border-green-200 border text-xs">Approved</Badge>;
  }
  if (task.approvalStatus === "rejected") {
    return <Badge className="bg-red-100 text-red-800 border-red-200 border text-xs">Rejected</Badge>;
  }
  return <Badge className="bg-gray-100 text-gray-600 border text-xs">{task.status}</Badge>;
}

function TaskCard({ task }: { task: AgentTask }) {
  const [expanded, setExpanded] = useState(false);
  const queryClient = useQueryClient();

  const approveMutation = useMutation({
    mutationFn: (decision: "approved" | "rejected") =>
      apiRequest("POST", `/api/agents/tasks/${task.id}/approve`, { decision }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-tasks-admin"] });
    },
  });

  const isPending = task.status === "awaiting_approval";
  const summary = task.outputSummary ?? "";
  const isTruncatable = summary.length > 180;
  const displaySummary = isTruncatable && !expanded ? summary.slice(0, 180) + "…" : summary;

  return (
    <div
      className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm space-y-3"
      data-testid={`task-card-${task.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Sparkles className="h-4 w-4 text-violet-500 shrink-0" />
            <span className="text-sm font-semibold text-gray-800">
              Kid Progress Summary
            </span>
            {statusBadge(task)}
          </div>
          <p className="text-xs text-gray-400">
            {new Date(task.createdAt).toLocaleString()} · {task.tokenCost} tokens
          </p>
        </div>

        {isPending && (
          <div className="flex gap-2 shrink-0">
            <Button
              size="sm"
              variant="outline"
              className="text-red-600 border-red-200 hover:bg-red-50"
              disabled={approveMutation.isPending}
              onClick={() => approveMutation.mutate("rejected")}
              data-testid={`btn-reject-${task.id}`}
            >
              <XCircle className="h-3.5 w-3.5 mr-1" />
              Reject
            </Button>
            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-white"
              disabled={approveMutation.isPending}
              onClick={() => approveMutation.mutate("approved")}
              data-testid={`btn-approve-${task.id}`}
            >
              <CheckCircle className="h-3.5 w-3.5 mr-1" />
              Approve
            </Button>
          </div>
        )}
      </div>

      {summary && (
        <div className="bg-gray-50 rounded-lg px-4 py-3">
          <p className="text-sm text-gray-700 leading-relaxed">{displaySummary}</p>
          {isTruncatable && (
            <button
              className="mt-1 text-xs text-violet-600 flex items-center gap-1 hover:underline"
              onClick={() => setExpanded((e) => !e)}
            >
              {expanded ? (
                <><ChevronUp className="h-3 w-3" />Show less</>
              ) : (
                <><ChevronDown className="h-3 w-3" />Show more</>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function TaskQueue() {
  const [filter, setFilter] = useState<StatusFilter>("awaiting_approval");

  const { data, isLoading } = useQuery<AgentTask[]>({
    queryKey: ["agent-tasks-admin", filter],
    queryFn: async () => {
      const params = filter === "all" ? "" : `?status=${filter}`;
      const res: any = await apiRequest("GET", `/api/agents/tasks${params}`);
      return asArray<AgentTask>(res.data);
    },
    refetchInterval: filter === "awaiting_approval" ? 15000 : false,
  });

  const tasks = asArray<AgentTask>(data);

  return (
    <div className="space-y-4">
      {/* Status filter toggle */}
      <div className="flex flex-wrap gap-2">
        {(Object.keys(STATUS_LABELS) as StatusFilter[]).map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filter === s
                ? "bg-violet-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            data-testid={`filter-${s}`}
          >
            {STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2].map((i) => <Skeleton key={i} className="h-32 w-full rounded-xl" />)}
        </div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-16 text-gray-400" data-testid="ai-task-empty">
          <Sparkles className="h-8 w-8 mx-auto mb-3 text-gray-300" />
          <p className="font-medium text-gray-500">No tasks pending review.</p>
          <p className="text-sm mt-1">Coaches can request AI summaries from player detail pages.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {tasks.map((task) => (
            <TaskCard key={task.id} task={task} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function AiTaskQueuePage() {
  return (
    <div
      className="px-4 py-6 md:px-6 md:py-8 max-w-3xl mx-auto space-y-6"
      data-testid="ai-task-queue-page"
    >
      <div>
        <h1 className="text-2xl font-bold text-gray-900" data-testid="ai-task-queue-heading">
          AI Task Queue
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Review and approve AI-generated progress summaries before they're visible to coaches.
        </p>
      </div>

      <Tabs defaultValue="queue" className="w-full">
        <TabsList className="bg-white border border-gray-200 rounded-lg p-1 h-auto mb-2">
          <TabsTrigger value="queue" data-testid="tab-queue">Queue</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
        </TabsList>
        <TabsContent value="queue">
          <TaskQueue />
        </TabsContent>
        <TabsContent value="activity">
          <ActivityFeed />
        </TabsContent>
      </Tabs>
    </div>
  );
}
